import { Component, OnInit, OnDestroy } from '@angular/core';
import { KpiDataService } from 'src/app/services/kpi-data.service';
import { ChartOptions } from 'src/app/shared/data/dashboard/default';
import { TranslateService } from '@ngx-translate/core';
import { LocalizationDataService } from '../../../services/data/localization-data.service';
import { Subscription } from 'rxjs';

interface SkillGapData {
  job_position: string;
  required_skills_count: number;
  acquired_skills_count: number;
  skills_gap_count: number;
}

interface AssessmentMarks {
  assessment_name: string;
  minimum_marks_required: number;
  average_marks_taken: number;
}

@Component({
  selector: 'app-assessment-kpis',
  templateUrl: './kpis.component.html',
  styleUrls: ['./kpis.component.scss']
})
export class AssessmentKpisComponent implements OnInit, OnDestroy {
  data: { skills_gap: SkillGapData[]; assessment_marks: AssessmentMarks[] } | null = null;
  public skillGaphChartOptions!: ChartOptions;
  public radarChartOptions!: ChartOptions;
  public jobPositions: string[] = [];
  public selectedJobPosition: string = '';
  public filteredData: SkillGapData[] = [];
  private languageSubscription!: Subscription;

  constructor(
    private kpiDataService: KpiDataService,
    private translate: TranslateService,
    private localizationDataService: LocalizationDataService
  ) {}

  ngOnInit(): void {
    this.fetchKpiData();

    // Listen for language changes and update charts
    this.languageSubscription = this.localizationDataService.currentLanguage$.subscribe(() => {
      this.updateChartTitles();
      this.reinitializeCharts();
    });
  }

  fetchKpiData(): void {
    this.kpiDataService.getAssessmentKpisData().subscribe(
      (res) => {
        if (res) {
          this.data = res;

          // Extract unique job positions
          this.jobPositions = Array.from(
            new Set((this.data?.skills_gap || []).map((item: SkillGapData) => String(item.job_position)))
          );

          this.selectedJobPosition = this.jobPositions.length > 0 ? this.jobPositions[0] : '';

          this.updateFilteredData();
          this.reinitializeCharts(); // Initialize charts after data is loaded
        }
      },
      (error) => {
        console.error('Error fetching KPI data:', error);
      }
    );
  }

  onJobPositionChange(): void {
    this.updateFilteredData();
    this.reinitializeCharts();
  }

  updateFilteredData(): void {
    if (!this.data) return;
    this.filteredData = this.data.skills_gap.filter((job) => job.job_position === this.selectedJobPosition);
  }

  async reinitializeCharts(): Promise<void> {
    await this.updateChartTitles(); // Ensure titles are translated before reinitializing
    this.initializeSkillGapChart();
    this.initializeRadarChart();
  }

  async updateChartTitles(): Promise<void> {
    const translations = await this.translate.get([
      'Skills Gap Analysis by Job Position',
      'Required Skills',
      'Acquired Skills',
      'Skills Gap',
      'Assessment Marks Radar Chart',
      'Minimum Marks Required',
      'Average Marks Taken'
    ]).toPromise();

    if (this.skillGaphChartOptions) {
      this.skillGaphChartOptions.title.text = translations['Skills Gap Analysis by Job Position'];
      this.skillGaphChartOptions.series[0].name = translations['Required Skills'];
      this.skillGaphChartOptions.series[1].name = translations['Acquired Skills'];
      this.skillGaphChartOptions.series[2].name = translations['Skills Gap'];
    }

    if (this.radarChartOptions) {
      this.radarChartOptions.title.text = translations['Assessment Marks Radar Chart'];
      this.radarChartOptions.series[0].name = translations['Minimum Marks Required'];
      this.radarChartOptions.series[1].name = translations['Average Marks Taken'];
    }
  }

  initializeSkillGapChart(): void {
    if (!this.filteredData.length) return;

    const jobPositions = this.filteredData.map((item) => item.job_position);
    const requiredSkills = this.filteredData.map((item) => item.required_skills_count);
    const acquiredSkills = this.filteredData.map((item) => item.acquired_skills_count);
    const skillsGap = this.filteredData.map((item) => item.skills_gap_count);

    this.skillGaphChartOptions = {
      series: [
        { name: this.translate.instant('Required Skills'), data: requiredSkills },
        { name: this.translate.instant('Acquired Skills'), data: acquiredSkills },
        { name: this.translate.instant('Skills Gap'), data: skillsGap }
      ],
      chart: { type: "bar", height: 350 },
      colors: ['rgb(25, 135, 84)', 'rgb(13, 202, 240)', 'rgb(220, 53, 69)'],
      plotOptions: {
        bar: { horizontal: false, columnWidth: "55%", dataLabels: { position: "top" } }
      },
      dataLabels: { enabled: false },
      xaxis: { categories: jobPositions, labels: { show: false } },
      title: { text: this.translate.instant("Skills Gap Analysis by Job Position") }
    };
  }

  initializeRadarChart(): void {
    if (!this.data || !this.data.assessment_marks.length) return;

    const assessments = this.data.assessment_marks.map((item) => item.assessment_name);
    const minimumMarks = this.data.assessment_marks.map((item) => item.minimum_marks_required);
    const averageMarks = this.data.assessment_marks.map((item) => item.average_marks_taken);

    this.radarChartOptions = {
      series: [
        { name: this.translate.instant("Minimum Marks Required"), data: minimumMarks },
        { name: this.translate.instant("Average Marks Taken"), data: averageMarks }
      ],
      chart: { type: "radar", height: 350 },
      plotOptions: {
        radar: { polygons: { fill: { colors: ['#e9e9e9', '#f4f4f4'] } } }
      },
      dataLabels: { enabled: false },
      xaxis: { categories: assessments, labels: { show: false } },
      title: { text: this.translate.instant("Assessment Marks Radar Chart") }
    };
  }

  ngOnDestroy(): void {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }
}
